/**
 * Main-window tabs — what is persisted, and how.
 *
 * The signed-in main window shows a browser-like tab strip
 * (components/popout/MainWindowTabs.tsx). One tab is the live app page
 * (the "primary" / home tab, whose page keeps living in localStorage.activeTab
 * as it always has); it starts first but can be moved like any other tab.
 * Every other tab is a page pop-out in an iframe. Which extra tabs are open,
 * the whole strip's order (including where the home tab sits), which tabs are
 * pinned, and which one is in front are remembered per user so a reload — or
 * signing in again on the same browser — comes back the same. Signing out
 * forgets them.
 *
 * STORED SHAPE  { tabs: [key…], active, primary, pinned? }
 *   tabs     the iframe tabs' page keys, in strip order (the home tab is not one)
 *   primary  where the home tab sits in the whole strip (0 = first)
 *   active   index into the whole strip
 *   pinned   how many tabs at the front of the whole strip are pinned
 * Records written before `primary` existed had the home tab first and pinned,
 * and counted `pinned` over `tabs` only; they still load (home first, and
 * pinned along with them when they had pinned tabs).
 */
import { appTabKey, parseAppTabKey, type AppTabSpec } from './popout';

/** Including the primary tab. More than this and the strip stops being usable. */
export const MAX_MAIN_TABS = 10;
const STORAGE_PREFIX = 'weavein:main-tabs:';

export interface PersistedMainTabs {
  /** The extra (iframe) tabs, in strip order. The primary tab is not one of them. */
  tabs: AppTabSpec[];
  /** Index into the whole strip (primary tab included). */
  active: number;
  /** Where the primary (home) tab sits in the whole strip, 0…tabs.length. Missing = 0. */
  primary?: number;
  /** How many tabs at the front of the whole strip (primary tab included) are pinned. Omitted when none. */
  pinned?: number;
}

const isIndex = (v: unknown, max: number): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= max;

/** Storage key for one user, or null when the id cannot be used in a key. */
export function mainTabsStorageKey(userId: string | number | null | undefined): string | null {
  if (userId === null || userId === undefined) return null;
  const id = String(userId);
  return /^[A-Za-z0-9_.@-]{1,128}$/.test(id) ? `${STORAGE_PREFIX}${id}` : null;
}

export function loadMainTabs(userId: string | number | null | undefined, storage?: Storage): PersistedMainTabs {
  const empty: PersistedMainTabs = { tabs: [], active: 0 };
  const key = mainTabsStorageKey(userId);
  if (!key) return empty;
  try {
    const raw = (storage ?? window.localStorage).getItem(key);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as { tabs?: unknown; active?: unknown; pinned?: unknown; primary?: unknown } | null;
    if (!parsed || !Array.isArray(parsed.tabs)) return empty;
    const tabs = parsed.tabs
      .slice(0, MAX_MAIN_TABS * 2)
      .map(parseAppTabKey)
      .filter((s): s is AppTabSpec => s !== null)
      .slice(0, MAX_MAIN_TABS - 1);
    const active = isIndex(parsed.active, tabs.length) ? parsed.active : 0;
    const rawPinned = typeof parsed.pinned === 'number' && Number.isInteger(parsed.pinned) && parsed.pinned > 0 ? parsed.pinned : 0;
    let primary = 0;
    let pinned = 0;
    if (parsed.primary === undefined) {
      // Older record: the home tab was first and always pinned, and `pinned`
      // counted the iframe tabs only. Keep the home tab with them when there were any.
      const framesPinned = Math.min(rawPinned, tabs.length);
      pinned = framesPinned ? framesPinned + 1 : 0;
    } else {
      primary = isIndex(parsed.primary, tabs.length) ? parsed.primary : 0;
      pinned = Math.min(rawPinned, tabs.length + 1);
    }
    return pinned ? { tabs, active, primary, pinned } : { tabs, active, primary };
  } catch {
    return empty;
  }
}

export function saveMainTabs(userId: string | number | null | undefined, state: PersistedMainTabs, storage?: Storage): void {
  const key = mainTabsStorageKey(userId);
  if (!key) return;
  try {
    const store = storage ?? window.localStorage;
    if (!state.tabs.length) {
      store.removeItem(key);
      return;
    }
    const pinned = Math.min(state.pinned ?? 0, state.tabs.length + 1);
    const primary = isIndex(state.primary, state.tabs.length) ? state.primary : 0;
    const record = { tabs: state.tabs.map(appTabKey), active: state.active, primary };
    store.setItem(key, JSON.stringify(pinned > 0 ? { ...record, pinned } : record));
  } catch {
    /* private mode / quota — the tabs just won't survive a reload */
  }
}

export function clearMainTabs(userId: string | number | null | undefined, storage?: Storage): void {
  const key = mainTabsStorageKey(userId);
  if (!key) return;
  try {
    (storage ?? window.localStorage).removeItem(key);
  } catch {
    /* ignore */
  }
}
